import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '@mui/joy';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import Papa from 'papaparse';

import Card from 'components/core/Card';
import Stack from 'components/core/Stack';
import Sheet from 'components/core/Sheet';
import Table from 'components/core/Table';
import Checkbox from 'components/core/Checkbox';
import IconButton from 'components/core/IconButton';
import SortCarets from 'components/core/SortCarets';
import Typography from 'components/core/Typography';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import Button from 'components/core/Button';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import Observation from 'domain/Observation';
import { CircularProgress } from '@mui/joy';

import CommonUtils from 'utils/CommonUtils';

const ObservationsTable = ({ 
    observations, 
    maxHeight, 
    taxonomyNameId, 
    showDownloadButton, 
    onLoadMore, 
    hasMoreRecords,
    isLoading,
    totalCount
}) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [sorting, setSorting] = useState({ field: 'speciesName', direction: 'asc' });
    const [checkedRows, setCheckedRows] = useState(new Set());
    const tableRef = useRef(null);
    const loadingRef = useRef(null);

    const data = observations.map(observation => {
        const tableData = observation.getTableValues();
        return {...tableData , id: observation.id};
    });

    useEffect(() => {
        if (!hasMoreRecords || !onLoadMore || !loadingRef.current) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && !isLoading && hasMoreRecords) {
                    onLoadMore();
                }
            },
            { threshold: 0.1 }
        );

        observer.observe(loadingRef.current);
        
        return () => {
            if (loadingRef.current) {
                observer.unobserve(loadingRef.current);
            }
        };
    }, [hasMoreRecords, onLoadMore, isLoading]);

    const handleCheckRow = (id) => {
        const newChecked = new Set(checkedRows);
        if (newChecked.has(id)) {
            newChecked.delete(id);
        } else {
            newChecked.add(id);
        }
        setCheckedRows(newChecked);
    };

    const handleCheckAll = () => {
        if (checkedRows.size === data.length) {
            setCheckedRows(new Set());
        } else {
            setCheckedRows(new Set(data.map(row => row.id)));
        }
    };

    const handleSort = (payload) => {
        setSorting(payload);
    };

    const getTownCount = () => {
        const countedTowns = []
        observations.forEach(observation => {
            const town = observation.getByPath(['town', 'label'])
            if (!countedTowns.includes(town) && town) {
                countedTowns.push(town)
            }
        })
        return countedTowns.length
    }

    const columns = [
        { key: 'checkbox', label: <Checkbox 
            style={{ marginBottom: 16 }}
            checked={data.length > 0 && checkedRows.size === data.length}
            indeterminate={checkedRows.size > 0 && checkedRows.size < data.length}
            onChange={handleCheckAll}
            variant="outlined"
        />, width: '40px' },
        { key: 'name', label: 'Taxon Name', width: '120px', sortable: true },
        { key: 'location', label: 'Location', width: '140px', sortable: true },
        { key: 'locationParent', label: 'Location Parent', width: '140px', sortable: true },
        { key: 'locationGrandparent', label: 'Location Grandparent', width: '140px', sortable: true },
        { key: 'town', label: 'Town', width: '75px', sortable: true },
        { key: 'date', label: 'Date', width: '100px', sortable: true },
        { key: 'observedBy', label: 'Observed By', width: '150px', sortable: true },
        { key: 'action', label: '', width: '40px' },
    ];

    const sortedData = data.sort((a, b) => {
        if (!sorting.field) return 0;
        
        const aValue = a[sorting.field];
        const bValue = b[sorting.field];
        
        if (typeof aValue === 'number') {
            return sorting.direction === 'asc' ? aValue - bValue : bValue - aValue;
        }
        
        return sorting.direction === 'asc' 
            ? String(aValue).localeCompare(String(bValue))
            : String(bValue).localeCompare(String(aValue));
    });

    const downloadSelected = () => {
        const selectedObservations = observations.filter(obs => checkedRows.has(obs.id));
        const fields = {
            name: 'Name',
            location: 'Location',
            locationParent: 'Location Parent',
            locationGrandparent: 'Location Grandparent',
            town: 'Town',
            lat: 'Latitude',
            lng: 'Longitude',
            locationNotes: 'Location Notes',
            date: 'Date',
            observedBy: 'Observed By',
            eventType: 'Event Type',
            eventName: 'Event Name'
        }
        const csvData = selectedObservations.map(observation => {
            const tableValues = observation.getTableValues();
            const row = {};
            Object.keys(fields).forEach(key => {
                row[fields[key]] = tableValues[key] || '';
            });
            return row;
        });
    
        if (csvData.length === 0) return;
    
        const csv = Papa.unparse(csvData);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        
        link.setAttribute('href', url);
        link.setAttribute('download', `observations-${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <>
            <Sheet sx={{width: '100%', background: 'none'}}>
                <Stack
                    direction="row"
                    sx={{ mb: 2 }}
                    spacing={10}
                >
                    <Typography>
                        <Typography variant='h3'>
                            Observations
                        </Typography>
                        <Typography variant='h4'>
                            {totalCount || observations.length} Observations / {getTownCount()} Towns
                        </Typography>
                    </Typography>
                    <Stack
                        direction="row"
                        spacing={1}
                        style={{ marginLeft: 'auto' }}
                    >
                        {taxonomyNameId && (
                            <Button onClick={() => {navigate(`/taxonomy/${taxonomyNameId}/add-observation`)}}>
                                + Add Entry
                            </Button>
                        )}
                        {showDownloadButton && (
                            <Button onClick={downloadSelected}>
                                <SaveAltIcon/>
                            </Button>
                        )}
                    </Stack>
                </Stack>
            </Sheet>
            <Sheet sx={{ width: '100%', background: 'none', mt: 2, mb: 2 }}>
                <Card sx={{ maxHeight: maxHeight, overflow: 'auto'}} ref={tableRef}>
                    <Table>
                        <thead>
                            <tr>
                                {columns.map(column => (
                                    <th key={column.key} style={{ width: column.width, whiteSpace: 'normal', wordWrap: 'break-word' }}>
                                        <Stack 
                                            direction="row" 
                                            sx={{
                                                '@media (max-width: 1350px)': {
                                                    flexDirection: 'column',
                                                    alignItems: 'flex-start'
                                                }
                                            }}
                                            alignItems="center"
                                            spacing={1}
                                        >
                                            {column.label}
                                            {column.sortable && (
                                                <SortCarets
                                                    field={column.key}
                                                    sorting={sorting}
                                                    setSorting={handleSort}
                                                />
                                            )}
                                        </Stack>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {sortedData.map(observation => (
                                <tr key={observation.id} 
                                    style={{
                                        backgroundColor: checkedRows.has(observation.id) ? 
                                            theme.palette.primary[50] : 'inherit'
                                    }}
                                >
                                    <td>
                                        <Checkbox 
                                            checked={checkedRows.has(observation.id)}
                                            onChange={() => handleCheckRow(observation.id)}
                                            variant="outlined"
                                        />
                                    </td>
                                    {columns.slice(1, -1).map(column => (
                                        <td key={column.key} style={{ width: column.width }}>
                                            {observation[column.key]}
                                        </td>
                                    ))}
                                    <td>
                                        <IconButton size="md" onClick={() => {navigate(`/observation/${observation.id}`)}}>
                                            <ArrowOutwardIcon />
                                        </IconButton>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                    
                    {hasMoreRecords && (
                        <div 
                            ref={loadingRef} 
                            style={{ 
                                textAlign: 'center', 
                                padding: '20px',
                                height: '60px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            {isLoading ? (
                                <CircularProgress size="sm" />
                            ) : (
                                <Typography color="neutral" level="body-sm">
                                    Scroll for more...
                                </Typography>
                            )}
                        </div>
                    )}
                </Card>
            </Sheet>
        </>
    );
}

ObservationsTable.propTypes = {
    observations: PropTypes.array.isRequired,
    maxHeight: PropTypes.string,
    taxonomyNameId: PropTypes.string,
    showDownloadButton: PropTypes.bool,
    onLoadMore: PropTypes.func,
    hasMoreRecords: PropTypes.bool,
    isLoading: PropTypes.bool,
    totalCount: PropTypes.number
};

ObservationsTable.defaultProps = {
    maxHeight: '35vh',
    taxonomyNameId: null,
    showDownloadButton: false,
    onLoadMore: null,
    hasMoreRecords: false,
    isLoading: false,
    totalCount: 0
};

export default ObservationsTable;